import React, { useEffect, useState } from "react";
import api from "../utils/api";
import './Users.css';

interface User {
  id: number;
  name: string;
  email: string;
  phone: string;
  type: string;
  status: string;
  createdAt?: string;
  lastLogin?: string;
  bookings?: number;
}

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterAndSort();
  }, [users, searchTerm, typeFilter, statusFilter, sortBy]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const resp = await api.getUsers(1, 100);
      if (Array.isArray(resp.users)) {
        const list = resp.users.map((u: any) => ({
          id: u.user_id || u.id,
          name: `${u.first_name || ''} ${u.last_name || ''}`.trim() || 'Unknown',
          email: u.email || '',
          phone: u.phone || '',
          type: u.user_type || 'customer',
          status: u.status || 'active',
          createdAt: u.created_at,
          lastLogin: u.last_login,
          bookings: u.booking_count || 0
        }));
        setUsers(list);
        setFilteredUsers(list);
      }
    } catch (e: any) {
      console.warn('users fetch failed', e);
      const status = e?.response?.status;
      if (status === 401) setError('Not authenticated — please login as admin');
      else if (status === 403) setError('Access denied — your account is not an admin');
      else setError(e?.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filterAndSort = () => {
    let filtered = [...users];

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(u => 
        u.name.toLowerCase().includes(term) ||
        u.email.toLowerCase().includes(term) ||
        u.phone.includes(term)
      );
    }

    if (typeFilter !== 'all') {
      filtered = filtered.filter(u => u.type === typeFilter);
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(u => u.status === statusFilter);
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'email':
          return a.email.localeCompare(b.email);
        case 'status':
          return a.status.localeCompare(b.status);
        case 'bookings':
          return (b.bookings || 0) - (a.bookings || 0);
        default:
          return 0;
      }
    });

    setFilteredUsers(filtered);
  };

  const handleViewDetails = (user: User) => {
    setSelectedUser(user);
    setDetailsOpen(true);
  };

  const getStatusClass = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'badge-success';
      case 'suspended':
        return 'badge-danger';
      case 'inactive':
        return 'badge-secondary';
      default:
        return 'badge-secondary';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'admin':
        return '👑';
      case 'agent':
        return '🤝';
      default:
        return '👤';
    }
  };

  return (
    <div className="users-page">
      <div className="page-header">
        <div>
          <h1>Users</h1>
          <p>Manage registered users and their permissions</p>
        </div>
        <button className="btn-primary">
          <span>➕</span> Invite User
        </button>
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <span className="search-icon">🔍</span>
          <input
            type="text"
            placeholder="Search by name, email, phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
            <option value="all">All Types</option>
            <option value="customer">Customer</option>
            <option value="agent">Agent</option>
            <option value="admin">Admin</option>
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="suspended">Suspended</option>
          </select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="name">Name</option>
            <option value="email">Email</option>
            <option value="status">Status</option>
            <option value="bookings">Bookings</option>
          </select>
        </div>
      </div>

      <div className="stats-mini">
        <div className="stat-mini-item">
          <span className="stat-mini-label">Total Users</span>
          <span className="stat-mini-value">{filteredUsers.length}</span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Active</span>
          <span className="stat-mini-value">
            {filteredUsers.filter(u => u.status === 'active').length}
          </span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Customers</span>
          <span className="stat-mini-value">
            {filteredUsers.filter(u => u.type === 'customer').length}
          </span>
        </div>
        <div className="stat-mini-item">
          <span className="stat-mini-label">Agents</span>
          <span className="stat-mini-value">
            {filteredUsers.filter(u => u.type === 'agent').length}
          </span>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading users...</p>
        </div>
      )}

      {error && (
        <div className="error-state">
          <span className="error-icon">⚠️</span>
          <p>{error}</p>
          <button onClick={loadUsers} className="btn-outline">Retry</button>
        </div>
      )}

      {!loading && !error && (
        <div className="users-grid">
          {filteredUsers.map((user) => (
            <div key={user.id} className="user-card">
              <div className="card-header">
                <div className="user-type">
                  <span className="type-icon">{getTypeIcon(user.type)}</span>
                  <span className="type-label">{user.type.toUpperCase()}</span>
                </div>
                <span className={`status-badge ${getStatusClass(user.status)}`}>
                  {user.status}
                </span>
              </div>
              <div className="card-body">
                <div className="user-avatar-large">
                  {user.name.charAt(0)}
                </div>
                <div className="user-info">
                  <div className="user-name">{user.name}</div>
                  <div className="user-email">{user.email}</div>
                  <div className="user-phone">{user.phone || 'No phone'}</div>
                </div>
                <div className="user-stats">
                  <div className="stat">
                    <span className="stat-value">{user.bookings || 0}</span>
                    <span className="stat-label">Bookings</span>
                  </div>
                  {user.createdAt && (
                    <div className="stat">
                      <span className="stat-value">
                        {new Date(user.createdAt).toLocaleDateString()}
                      </span>
                      <span className="stat-label">Joined</span>
                    </div>
                  )}
                </div>
              </div>
              <div className="card-footer">
                <button className="btn-view" onClick={() => handleViewDetails(user)}>
                  View Details
                </button>
                <div className="action-buttons">
                  <button className="btn-icon" title="Edit">✏️</button>
                  <button className="btn-icon" title="Suspend">🔒</button>
                </div>
              </div>
            </div>
          ))}

          {filteredUsers.length === 0 && (
            <div className="empty-state">
              <span className="empty-icon">👥</span>
              <h3>No users found</h3>
              <p>Try adjusting your filters</p>
            </div>
          )}
        </div>
      )}

      {/* Details Modal */}
      {detailsOpen && selectedUser && (
        <div className="modal-overlay" onClick={() => setDetailsOpen(false)}>
          <div className="modal-content user-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>User Details</h3>
              <button className="close-btn" onClick={() => setDetailsOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="user-details">
                <div className="detail-avatar">
                  {selectedUser.name.charAt(0)}
                </div>
                <div className="detail-info">
                  <p><strong>Name:</strong> {selectedUser.name}</p>
                  <p><strong>Email:</strong> {selectedUser.email}</p>
                  <p><strong>Phone:</strong> {selectedUser.phone || 'N/A'}</p>
                  <p><strong>Type:</strong> {selectedUser.type}</p>
                  <p><strong>Status:</strong> 
                    <span className={`status-badge ${getStatusClass(selectedUser.status)}`}>
                      {selectedUser.status}
                    </span>
                  </p>
                  <p><strong>Total Bookings:</strong> {selectedUser.bookings || 0}</p>
                  {selectedUser.createdAt && (
                    <p><strong>Joined:</strong> {new Date(selectedUser.createdAt).toLocaleDateString()}</p>
                  )}
                  {selectedUser.lastLogin && (
                    <p><strong>Last Login:</strong> {new Date(selectedUser.lastLogin).toLocaleString()}</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;